import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ukTime } from "@/lib/time";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHead, Card, Money, gbp, Pill, LiveDot, TabBar, RangeToggle } from "@/components/hub/Shell";
import {
  Loader2, RefreshCw, ChevronRight, Layers, AlertTriangle, FileWarning,
  Clock, Building2, Handshake, Rows3, Columns3, TrendingUp, Landmark,
  ShieldQuestion, BadgePoundSterling, RotateCcw,
} from "lucide-react";

// Stage colour runs down the same navy scale as the rest of the Hub — slate (nothing yet) →
// faded navy (waiting) → brand blue (moving) → teal (settled) → deep navy (dead).
const STAGE_META = {
  new:        { label: "Submitted",    colour: "#94a3b8" },
  sent:       { label: "With lenders", colour: "#7da9ff" },
  in_review:  { label: "Under assessment", colour: "#5B86D6" },
  offer_made: { label: "Offer issued", colour: "#0055FF" },
  completed:  { label: "Funded",       colour: "#14b8a6" },
  declined:   { label: "Declined",     colour: "#0B1220" },
};
const ORDER = ["new", "sent", "in_review", "offer_made", "completed", "declined"];
const FLAG_META = {
  needs_review:       { label: "Needs review",   tone: "act",   Icon: ShieldQuestion },
  awaiting_lender:    { label: "No lender yet",  tone: "wait",  Icon: Clock },
  needs_rehome:       { label: "Needs a new home", tone: "act", Icon: RotateCcw },
  stalled:            { label: "Gone quiet",     tone: "wait",  Icon: AlertTriangle },
  docs_outstanding:   { label: "Docs chased",    tone: "slate", Icon: FileWarning },
  income_unconfirmed: { label: "Fee to confirm", tone: "wait",  Icon: BadgePoundSterling },
  loss_making:        { label: "Under water",    tone: "dead",  Icon: AlertTriangle },
};

export default function HubPipeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(0);
  const [stage, setStage] = useState("open");
  const [view, setView] = useState("ledger");
  const [open, setOpen] = useState(null);
  const [beat, setBeat] = useState(0);
  const lastNet = useRef(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const r = await api.get(`/hub/pipeline/value${days ? `?days=${days}` : ""}`);
      const net = r.data?.totals?.open?.net_gbp;
      if (lastNet.current !== null && net !== lastNet.current) setBeat((b) => b + 1);
      lastNet.current = net;
      setData(r.data);
    } catch {
      setData({ deals: [], stages: [], totals: null });
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load(true), 20000);
    const onChange = () => load(true);
    window.addEventListener("hub-leads:changed", onChange);
    return () => { clearInterval(t); window.removeEventListener("hub-leads:changed", onChange); };
  }, [load]);

  const totals = data?.totals;
  const byStage = useMemo(() => {
    const m = {};
    (data?.stages || []).forEach((s) => { m[s.key] = s; });
    return m;
  }, [data]);

  const deals = useMemo(() => {
    const all = data?.deals || [];
    if (stage === "all") return all;
    if (stage === "unverified") return all.filter((d) => d.unverified);
    const book = all.filter((d) => !d.unverified);
    if (stage === "open") return book.filter((d) => !["completed", "declined"].includes(d.stage));
    return book.filter((d) => d.stage === stage);
  }, [data, stage]);

  const tabs = useMemo(() => {
    const all = data?.deals || [];
    const book = all.filter((d) => !d.unverified);
    const unverified = all.length - book.length;
    return [
      { key: "open", label: "Live book", count: book.filter((d) => !["completed", "declined"].includes(d.stage)).length },
      ...ORDER.map((k) => ({ key: k, label: STAGE_META[k].label, dot: STAGE_META[k].colour, count: byStage[k]?.deals || 0 })),
      ...(unverified ? [{ key: "unverified", label: "Needs review", dot: "#0055FF", count: unverified }] : []),
      { key: "all", label: "Everything", count: all.length },
    ];
  }, [data, byStage]);

  if (loading && !data) {
    return <div className="grid place-items-center py-28 text-slate-400"><Loader2 className="size-6 animate-spin" /></div>;
  }

  const openT = totals?.open || {};
  const wonT = totals?.won || {};
  const monthT = totals?.won_month || {};
  const lostT = totals?.lost || {};

  return (
    <div className="space-y-7 pb-28" data-testid="hub-pipeline">
      <PageHead
        eyebrow="Pipeline"
        title="What the book is worth"
        sub="Every case priced off the fee the lender agreed when it was sent, less what the introducer and their recruiter are owed. Nothing here is typed in by hand — decline a case, move a stage or purge a dud and these figures move with it."
        right={
          <>
            <LiveDot label={`Updated ${fmtClock(data?.as_of)}`} testId="hub-pipeline-live" />
            <RangeToggle options={[0, 30, 90]} value={days} onChange={setDays} testPrefix="hub-pipeline-range" />
            <button
              onClick={() => load()}
              className="grid size-8 place-items-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900"
              aria-label="Refresh" data-testid="hub-pipeline-refresh"
            >
              <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </>
        }
      />

      {/* ── The four figures that matter ─────────────────────────── */}
      <div
        className="hub-card grid grid-cols-1 overflow-hidden rounded-[14px] border border-slate-200/70 bg-white sm:grid-cols-2 lg:grid-cols-4"
        data-testid="hub-pipeline-totals"
      >
        <Tile
          label="Live book · net to us"
          value={openT.net_gbp}
          tone="brand"
          beat={beat}
          sub={`${openT.deals || 0} open ${plural(openT.deals, "case")} · ${gbp(openT.funding_gbp, { compact: true })} of funding`}
          foot={footOpen(openT, totals?.unverified)}
          testId="hub-total-open"
        />
        <Tile
          label="Weighted forecast"
          value={openT.weighted_net_gbp}
          Icon={TrendingUp}
          sub="the same book, discounted by how far each case has actually got"
          foot="new 10% · with lenders 25% · in review 45% · offer 75%"
          testId="hub-total-forecast"
        />
        <Tile
          label="Funded this month"
          value={monthT.net_gbp}
          tone="good"
          sub={`${monthT.deals || 0} ${plural(monthT.deals, "deal")} settled since the 1st`}
          foot={wonT.income_unconfirmed_deals
            ? `${wonT.income_unconfirmed_deals} funded ${plural(wonT.income_unconfirmed_deals, "deal")} still on an estimated lender fee — confirm it on the case`
            : `${gbp(wonT.net_gbp, { compact: true })} net across ${wonT.deals || 0} funded all time`}
          testId="hub-total-month"
        />
        <Tile
          label="Declined"
          value={lostT.net_gbp}
          tone="lost"
          sub={`${lostT.deals || 0} ${plural(lostT.deals, "case")} · ${gbp(lostT.funding_gbp, { compact: true })} of funding didn't land`}
          foot={lostT.committed_cost_gbp ? `${gbp(lostT.committed_cost_gbp)} of per-lead fees still payable` : "nothing owed out on these"}
          testId="hub-total-lost"
        />
      </div>

      {/* ── Gross → introducer → net ─────────────────────────────── */}
      <Card label="Where the live book goes" Icon={Layers} tone="brand" testId="hub-pipeline-breakdown"
            right={<Pill tone="brand" testId="hub-breakdown-margin">{marginPct(openT)}% kept</Pill>}>
        <div className="mt-4 space-y-2.5">
          <Ladder label="Gross in from lenders" detail="fees frozen on each route when it was sent"
                  value={openT.gross_gbp} tone="in" pct={100} testId="hub-ladder-gross" />
          <Ladder label="Less introducer commission" detail="each partner on their own agreed model"
                  value={-(openT.introducer_gbp || 0)} tone="out"
                  pct={share(openT.introducer_gbp, openT.gross_gbp)} testId="hub-ladder-intro" />
          <Ladder label="Less network overrides" detail="recruiters' cut on partners they brought in"
                  value={-(openT.overrides_gbp || 0)} tone="out"
                  pct={share(openT.overrides_gbp, openT.gross_gbp)} testId="hub-ladder-overrides" />
          <div className="h-px bg-slate-200/80" />
          <Ladder label="Net to Connexis" detail="what completes into the business if the book lands"
                  value={openT.net_gbp} tone="net" pct={share(openT.net_gbp, openT.gross_gbp)} strong
                  testId="hub-ladder-net" />
        </div>
        {openT.estimated_deals > 0 && (
          <p className="mt-4 border-t border-slate-100 pt-3 text-[11px] leading-relaxed text-slate-400"
             data-testid="hub-ladder-estimate-note">
            {gbp(openT.estimated_net_gbp)} of that net figure sits on desks paying us a share of their own
            commission, forecast at the fee percentage set against each desk. It firms up the moment they
            confirm what they earned on the case.
          </p>
        )}
      </Card>

      {/* ── Filter + view ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <TabBar items={tabs} value={stage} onChange={setStage} testPrefix="hub-pipeline-tab" />
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5">
          {[["ledger", Rows3, "Ledger"], ["board", Columns3, "Board"]].map(([k, Icon, label]) => (
            <button key={k} onClick={() => setView(k)}
                    className={`inline-flex h-7 items-center gap-1.5 rounded-[10px] px-3 text-[11.5px] font-medium transition-colors ${
                      view === k ? "bg-[#0B1220] text-white" : "text-slate-500 hover:text-slate-800"}`}
                    data-testid={`hub-pipeline-view-${k}`}>
              <Icon className="size-3" /> {label}
            </button>
          ))}
        </div>
      </div>

      {view === "ledger"
        ? <Ledger deals={deals} open={open} setOpen={setOpen} />
        : <Board deals={data?.deals || []} byStage={byStage} />}
    </div>
  );
}

/* ─── Totals tile ──────────────────────────────────────────────── */
const TILE_TONE = { brand: "brand", good: "in", lost: "out", net: "net" };
function Tile({ label, value, sub, foot, tone = "net", Icon, beat, testId }) {
  return (
    <div className="relative min-w-0 border-b border-slate-100 p-5 last:border-b-0 sm:[&:nth-child(-n+2)]:border-b sm:[&:nth-child(n+3)]:border-b-0 lg:border-b-0 lg:border-r lg:border-slate-100 lg:last:border-r-0"
         data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="size-3 text-slate-400" />}
        <div className="truncate text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</div>
      </div>
      <div key={beat} className={`mt-2.5 inline-block ${beat ? "hub-flash" : ""}`}>
        <Money value={value} tone={TILE_TONE[tone] || "net"} size="xl" testId={`${testId}-value`} />
      </div>
      <div className="mt-2 text-[11.5px] leading-snug text-slate-500">{sub}</div>
      {foot && <div className="mt-2.5 border-t border-slate-100 pt-2 text-[10.5px] leading-snug text-slate-400">{foot}</div>}
    </div>
  );
}

/* ─── The gross → net ladder ───────────────────────────────────── */
/* Owner on the video: the bars "look sort of insignificant and basic". They were 6px on
   `bg-slate-100` — a track so faint the bar read as unfinished rather than as a measure. Now on
   the design system's own `.bar` (8px on `--line`), which gives the unfilled portion real weight,
   and they grow on first paint with a stagger so the section arrives rather than just appearing. */
const LADDER_TONE = { in: "var(--green)", out: "var(--mut-2)", net: "var(--accent)" };
function Ladder({ label, detail, value, tone, pct, strong, testId }) {
  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <div className={`text-[12.5px] sm:truncate ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>{label}</div>
        <div className="bar bar-grow mt-2">
          <i style={{ width: `${Math.max(1.5, Math.min(100, Math.abs(pct || 0)))}%`,
            background: LADDER_TONE[tone] || LADDER_TONE.net }} />
        </div>
        <div className="mt-2 text-[11px] text-slate-400 sm:truncate">{detail}</div>
      </div>
      <Money value={value} tone={tone} size={strong ? "md" : "sm"} className="w-[92px] shrink-0 text-right sm:w-[112px]" />
    </div>
  );
}

/* ─── Ledger ───────────────────────────────────────────────────── */
const COLS = "grid-cols-[minmax(0,1fr)_88px] lg:grid-cols-[minmax(0,1fr)_150px_136px_104px_104px_104px_112px_28px]";
function Ledger({ deals, open, setOpen }) {
  return (
    <div className="hub-card overflow-hidden rounded-[14px] border border-slate-200/70 bg-white" data-testid="hub-pipeline-ledger">
      <div className={`grid ${COLS} items-center gap-x-4 border-b border-slate-200/80 bg-slate-50/70 px-5 py-2.5 text-[9.5px] font-semibold uppercase tracking-[0.16em] text-slate-400`}>
        <div>Case</div>
        <div className="hidden lg:block">Came from</div>
        <div className="hidden lg:block">Lender</div>
        <div className="hidden text-right lg:block">Funding</div>
        <div className="hidden text-right lg:block">Gross in</div>
        <div className="hidden text-right lg:block">Introducer</div>
        <div className="text-right">Net to us</div>
        <div className="hidden lg:block" />
      </div>

      {deals.length === 0 ? (
        <div className="px-5 py-16 text-center text-[12.5px] text-slate-400" data-testid="hub-pipeline-empty">
          Nothing in this view.
        </div>
      ) : deals.map((d, i) => (
        <Row key={d.id} deal={d} index={i} expanded={open === d.id}
             onToggle={() => setOpen(open === d.id ? null : d.id)} />
      ))}
    </div>
  );
}

function Row({ deal, index, expanded, onToggle }) {
  const v = deal.value;
  const meta = STAGE_META[deal.stage] || STAGE_META.new;
  return (
    <div className={`border-b border-slate-100 last:border-b-0 ${expanded ? "bg-slate-50/60" : ""}`}>
      <button
        onClick={onToggle}
        className={`grid w-full ${COLS} items-center gap-x-4 px-5 py-3.5 text-left transition-colors hover:bg-slate-50`}
        data-testid={`hub-deal-row-${deal.id}`}
        style={{ animationDelay: `${Math.min(index, 12) * 18}ms` }}
      >
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <span className="size-1.5 shrink-0 rounded-full" style={{ background: meta.colour }} />
            <span className="sm:truncate text-[13.5px] font-semibold text-slate-900">{deal.business_name || deal.applicant_name}</span>
            {deal.source.direct && (
              <span className="shrink-0 rounded border border-[#0055FF]/25 bg-[#0055FF]/[0.07] px-1.5 text-[9px] font-bold uppercase tracking-wider text-[#0055FF]">Direct</span>
            )}
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
            <span className="sm:truncate">{deal.applicant_name}</span>
            <span className="text-slate-300">·</span>
            <span className="sm:truncate">{deal.finance_type}</span>
            <span className="hub-num text-slate-400 lg:hidden">{gbp(deal.amount_gbp, { compact: true })}</span>
            <span className="text-slate-300 lg:hidden">·</span>
            <span className="lg:hidden">{meta.label}</span>
            {(deal.flags || []).map((f) => {
              const fm = FLAG_META[f];
              if (!fm) return null;
              return (
                <span key={f} className={`inline-flex items-center gap-1 text-[10.5px] font-semibold ${
                  fm.tone === "wait" ? "text-[#3B63A8]" : fm.tone === "act" ? "text-[#0044CC]" : fm.tone === "dead" ? "text-[#0B1220]" : "text-slate-400"}`}
                      data-testid={`hub-deal-flag-${f}-${deal.id}`}>
                  <fm.Icon className="size-2.5" /> {fm.label}
                </span>
              );
            })}
          </div>
        </div>

        <div className="hidden min-w-0 lg:block">
          <div className="flex items-center gap-1.5 text-[12px] text-slate-700">
            {deal.source.direct ? <Landmark className="size-3 shrink-0 text-[#0055FF]" /> : <Handshake className="size-3 shrink-0 text-slate-400" />}
            <span className="truncate">{deal.source.name}</span>
          </div>
          <div className="mt-0.5 truncate text-[10.5px] text-slate-400">{deal.source.terms || (deal.source.direct ? "no commission to pay" : "—")}</div>
        </div>

        <div className="hidden min-w-0 lg:block">
          {deal.lender ? (
            <>
              <div className="flex items-center gap-1.5 text-[12px] text-slate-700">
                <Building2 className="size-3 shrink-0 text-slate-400" />
                <span className="truncate">{deal.lender.name}</span>
              </div>
              <div className="mt-0.5 truncate text-[10.5px] text-slate-400">
                {deal.lenders_count > 1 ? `${deal.lenders_count} desks · ` : ""}{(STAGE_META[deal.lender.status] || {}).label || deal.lender.status}
              </div>
            </>
          ) : <span className="text-[11.5px] text-slate-400">awaiting a desk</span>}
        </div>

        <Money value={deal.amount_gbp} tone="muted" className="hidden text-right lg:block" />
        <div className="hidden text-right lg:block">
          <Money value={v.gross_gbp} tone={v.priced ? "in" : "muted"} testId={`hub-deal-gross-${deal.id}`} />
          {v.gross_estimated && (
            <div className="text-[9.5px] uppercase tracking-[0.12em] text-slate-400"
                 title={v.lines?.[0]?.detail || "Estimated until the desk confirms what they earned"}
                 data-testid={`hub-deal-est-${deal.id}`}>est.</div>
          )}
        </div>
        <Money value={-(v.introducer_gbp + v.overrides_gbp)} tone={v.introducer_gbp + v.overrides_gbp ? "out" : "muted"}
               className="hidden text-right lg:block" testId={`hub-deal-out-${deal.id}`} />
        <div className="text-right">
          <Money value={v.net_gbp} tone={v.net_gbp < 0 ? "out" : "net"} size="md" className="font-semibold"
                 testId={`hub-deal-net-${deal.id}`} />
          <div className="hub-num mt-0.5 text-[10px] text-slate-400">{v.confidence_pct}% likely</div>
        </div>
        <ChevronRight className={`hidden size-3.5 text-slate-400 transition-transform lg:block ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="hub-rise border-t border-slate-200/70 bg-white px-5 py-4" data-testid={`hub-deal-detail-${deal.id}`}>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">How this deal pays</div>
              <div className="mt-3 divide-y divide-slate-100">
                {(v.lines || []).map((l) => (
                  <div key={l.key} className="flex items-start gap-4 py-2.5" data-testid={`hub-deal-line-${l.key}-${deal.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[12.5px] ${l.kind === "net" ? "font-semibold text-slate-900" : "text-slate-700"}`}>{l.label}</div>
                      {l.detail && <div className="mt-0.5 text-[11px] leading-snug text-slate-400">{l.detail}</div>}
                    </div>
                    {l.estimated && <Pill tone="wait" testId={`hub-deal-line-est-${deal.id}`}>Estimate</Pill>}
                    {l.kind === "pending"
                      ? <span className="w-[124px] shrink-0 text-right text-[11px] text-slate-400">not yet payable</span>
                      : <Money value={l.kind === "out" ? -l.amount_gbp : l.amount_gbp}
                               tone={l.kind === "in" ? "in" : l.kind === "out" ? "out" : "net"}
                               size={l.kind === "net" ? "md" : "sm"}
                               className={`w-[124px] shrink-0 text-right ${l.kind === "net" ? "font-semibold" : ""}`} />}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-slate-400">{v.basis}.{v.margin_pct !== null && v.margin_pct !== undefined ? ` We keep ${v.margin_pct}% of the lender fee on this one.` : ""}</p>

              {(deal.passed || []).length > 0 && (
                <div className="mt-5 rounded-xl border border-[#0B1220]/15 bg-slate-50 p-4" data-testid={`hub-deal-passed-${deal.id}`}>
                  <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0B1220]">
                    <RotateCcw className="size-3" /> Who passed, and why
                  </div>
                  <div className="mt-2.5 divide-y divide-slate-200/70">
                    {deal.passed.map((p, i) => (
                      <div key={`${p.name || "desk"}-${i}`} className="flex items-baseline justify-between gap-4 py-1.5"
                           data-testid={`hub-deal-passed-row-${deal.id}-${i}`}>
                        <span className="min-w-0 truncate text-[12.5px] text-slate-700">{p.name || "A desk"}</span>
                        <span className="shrink-0 text-right text-[11.5px] text-slate-500">{p.reason || "no reason given"}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2.5 text-[11px] leading-relaxed text-slate-500">
                    Nothing has been said to the client. Open the case to send it to a different desk, or
                    message the introducer about what would make it fundable.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3 rounded-xl border border-slate-200/70 bg-slate-50/60 p-4">
              <Detail k="Reference" v={deal.reference || "—"} mono />
              <Detail k="Funding sought" v={gbp(deal.amount_gbp)} mono />
              <Detail k="Stage" v={`${(STAGE_META[deal.stage] || {}).label} · ${v.confidence_pct}% likely`} />
              <Detail k="Age" v={`${deal.age_days ?? 0}d old · last touched ${deal.idle_days ?? 0}d ago`} />
              {deal.docs_open > 0 && <Detail k="Documents" v={`${deal.docs_open} outstanding`} />}
              <Link to={`/hub/leads/${deal.id}`}
                    className="inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-[#0B1220] px-3.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#0055FF]"
                    data-testid={`hub-deal-open-${deal.id}`}>
                Open the case <ChevronRight className="size-3" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ k, v, mono }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">{k}</span>
    <span className={`text-right text-[12px] text-slate-700 ${mono ? "hub-num" : ""}`}>{v}</span>
  </div>
);

/* ─── Board ────────────────────────────────────────────────────── */
function Board({ deals, byStage }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6" data-testid="hub-pipeline-board">
      {ORDER.map((key) => {
        const meta = STAGE_META[key];
        const s = byStage[key] || {};
        const items = deals.filter((d) => d.stage === key);
        return (
          <div key={key} className="flex min-h-[220px] flex-col rounded-[14px] border border-slate-200/70 bg-white/70">
            <div className="border-b border-slate-200/70 px-3.5 py-3">
              <div className="flex items-center justify-between gap-2">
                <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-700">
                  <span className="size-1.5 rounded-full" style={{ background: meta.colour }} />{meta.label}
                </div>
                <span className="hub-num text-[11px] text-slate-400">{s.deals || 0}</span>
              </div>
              <div className="mt-2 flex items-baseline justify-between gap-2">
                <Money value={s.net_gbp} tone={key === "declined" ? "out" : "net"} size="md" className="font-semibold" />
                <span className="text-[10px] text-slate-400">{s.confidence_pct}% likely</span>
              </div>
            </div>
            <div className="hub-scroll max-h-[58vh] flex-1 space-y-2 overflow-y-auto p-2.5" data-testid={`hub-pipeline-col-${key}`}>
              {items.length === 0
                ? <div className="py-8 text-center text-[11.5px] text-slate-400">Empty</div>
                : items.map((d) => (
                  <Link key={d.id} to={`/hub/leads/${d.id}`}
                        className="hub-card block rounded-xl border border-slate-200/70 bg-white p-3"
                        data-testid={`hub-pipeline-card-${d.id}`}>
                    <div className="sm:truncate text-[12.5px] font-semibold text-slate-900">{d.business_name || d.applicant_name}</div>
                    <div className="mt-0.5 text-[11px] sm:truncate text-slate-500">{d.source.direct ? "Connexis Direct" : d.source.name}</div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="hub-num text-[11px] text-slate-400">{gbp(d.amount_gbp, { compact: true })}</span>
                      <Money value={d.value.net_gbp} tone={d.value.net_gbp < 0 ? "out" : "net"} size="xs" className="font-semibold" />
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────────── */
function share(part, whole) {
  const w = Math.abs(Number(whole || 0));
  if (!w) return 0;
  return (Math.abs(Number(part || 0)) / w) * 100;
}
function marginPct(t) {
  const g = Number(t?.gross_gbp || 0);
  if (!g) return 0;
  return Math.round((Number(t?.net_gbp || 0) / g) * 100);
}
function plural(n, word) { return Number(n) === 1 ? word : `${word}s`; }
function footOpen(openT, unverified) {
  const bits = [];
  if (openT.unpriced_deals) bits.push(`${openT.unpriced_deals} not priced yet — no lender on it`);
  if (unverified?.deals) bits.push(`${unverified.deals} unverified ${plural(unverified.deals, "application")} held out of the book`);
  return bits.length ? bits.join(" · ") : "every open case has a lender fee attached";
}
function fmtClock(iso) {
  return ukTime(iso, "—");
}
